import { graphql } from "babel-plugin-relay/macro";
import React, { Suspense, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useFragment, useLazyLoadQuery, useQueryLoader } from "react-relay";
import { Navigate, useMatch } from "react-router-dom";
import styled from "styled-components";
import { ContentNodeScreen_NodeFragment$key } from "@generated/ContentNodeScreen_NodeFragment.graphql";
import { ContentNodeScreen_Query } from "@generated/ContentNodeScreen_Query.graphql";
import { usePageTitle } from "../../infecto-lms-webapp/hooks/usePageTitle";
import { selectProgressBar } from "../../infecto-lms-webapp/redux/slices/progress-bar.slice";
import {
	getRootAndContentNodePath,
	ROOT_AND_CONTENT_NODE_PARAMS,
} from "../../infecto-lms-webapp-impl/router/routes/auth/nodes.routes";
import { NodeLeftFrame } from "../components/left-frame/NodeLeftFrame.component";
import { RightFrameContainer } from "../components/right-frame/RightFrameContainer";
import { SkeletonRightFrame } from "../components/right-frame/SkeletonRightFrame";
import { PageDefaultContainer } from "../containers/PageDefaultContainer";
import { WideContainerWithTopBar } from "../containers/WideContainerWithTopBar.container";
import { useGenerateContentSubmission } from "../utils/useGenerateContentSubmission";

const QUERY = graphql`
	query ContentNodeScreen_Query($learnOpportunityId: ID!) {
		node(id: $learnOpportunityId) {
			id
			... on LearnOpportunityV2 {
				id
				...ContentNodeScreen_NodeFragment
			}
		}
		Viewer {
			Auth {
				currentUser {
					user {
						...DefaultContainer_UserDataFragment
						...UserStatusMessage_UserFragment
					}
				}
			}
		}
	}
`;

const LEARN_OPPORTUNITY_FRAGMENT = graphql`
	fragment ContentNodeScreen_NodeFragment on LearnOpportunityV2 {
		id
		root {
			id
			structureDefinition {
				title
			}
		}
		structureDefinition {
			title
		}
		typeDefinition {
			... on LearnOpportunityBranchTypeDefinition {
				children {
					id
					typeDefinition {
						... on LearnOpportunityELearningContentTypeDefinition {
							containedElementTypes
							contentNodeAdvancementResult {
								status
							}
						}
					}
				}
			}
		}
		...useGenerateContentSubmission_NodeFragment
	}
`;
export const ContentNodeScreen = () => {
	const contentNodeId = useMatch(getRootAndContentNodePath(ROOT_AND_CONTENT_NODE_PARAMS))?.params
		.contentId;

	const [isReady, setIsReady] = useState(false);
	const [isModulExpiredAndFailed, setIsModuleExpiredAndFailed] = useState(false);
	const query = useLazyLoadQuery<ContentNodeScreen_Query>(
		QUERY,
		{
			learnOpportunityId: contentNodeId! as string,
		},
		{ fetchPolicy: "network-only" },
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, loadQuery] = useQueryLoader<ContentNodeScreen_Query>(QUERY);

	const contentSubmissionProgressPercentage = useSelector(selectProgressBar);

	useEffect(() => {
		loadQuery({ learnOpportunityId: contentNodeId! as string }, { fetchPolicy: "network-only" });
	}, [contentSubmissionProgressPercentage, contentNodeId, loadQuery]);

	const learnOpportunity = useFragment<ContentNodeScreen_NodeFragment$key>(
		LEARN_OPPORTUNITY_FRAGMENT,
		query.node!,
	)!;

	if (!learnOpportunity) {
		return <Navigate to="*" />;
	}

	usePageTitle(learnOpportunity.structureDefinition.title);
	const nodeId = contentNodeId || learnOpportunity.id;
	const generateContentSubmission = useGenerateContentSubmission({
		learnOpportunity,
		loadQuery,
		setIsReady,
		nodeId,
	});
	const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

	useEffect(() => {
		generateContentSubmission();
	}, []);

	const rightFrameRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (isReady && isMobile && rightFrameRef.current) {
			rightFrameRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, [isReady, isMobile]);

	return (
		<PageDefaultContainer
			collapsed={false}
			screenName="learn-opportunity"
			containerId={learnOpportunity.id}
		>
			<WideContainerWithTopBar className="pr-0 pl-2">
				<div className="flex flex-column lg:flex-row">
					<NodeLeftFrame
						isContentNodeScreen={true}
						isModuleExpiredAndFailed={isModulExpiredAndFailed}
						rootId={learnOpportunity?.root?.id!}
						ignoreUserLearnState={false}
					/>

					<DescriptionAndAttachmentsSideBar className="col-12 xl:col-8 p-0 flex flex-column flex-grow-1">
						<Suspense fallback={<SkeletonRightFrame />}>
							{isReady && (
								<RightFrameContainer
									scrollRef={rightFrameRef}
									setIsModuleExpiredAndFailed={setIsModuleExpiredAndFailed}
									contentNodeId={learnOpportunity.id}
									scrollOnReady={isReady && isMobile}
								/>
							)}
						</Suspense>
					</DescriptionAndAttachmentsSideBar>
				</div>
			</WideContainerWithTopBar>
		</PageDefaultContainer>
	);
};

const DescriptionAndAttachmentsSideBar = styled.div`
	padding-bottom: 200px;
`;
